// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import { xScreen } from "@ces/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import * as React from "react";
import { ReactNode } from "react";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: PageLayoutProps) => {
    // XNG-Changes: pass isAuthenticated
    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state));

    return (
        <PageLayoutWrapper {...{ isAuthenticated }}>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && (
                <Page as="div">
                    <PageBreadcrumbs />
                </Page>
            )}

            {pageContent}
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    :has(.checkout-header) {
        main {
            margin-bottom: 44px;
        }
    }
    main {
        flex: 1 1 0%;
        /* XNG-CHANGES */
        ${({ isAuthenticated }: any) => {
            if (!isAuthenticated) {
                return css`
                    margin-bottom: 90px;
                    ${xScreen("lte", "md")} {
                        margin-bottom: 44px;
                    }
                `;
            }
            return css`
                margin-bottom: 45px;
            `;
        }}
    }

    @media print {
        display: block;
        > main {
            margin-bottom: 0;
            margin-top: 0;
            display: flex;
        }
    }
`;

export default PageLayout;
